class LuxuryHeader extends HTMLElement {
  constructor() {
    super();
    this.onScroll = this.onScroll.bind(this);
    this.openDrawer = this.openDrawer.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
    this.openSearch = this.openSearch.bind(this);
    this.closeSearch = this.closeSearch.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    this.drawer = this.querySelector('[data-drawer]');
    this.drawerOverlay = this.querySelector('[data-drawer-overlay]');
    this.drawerClose = this.querySelector('[data-drawer-close]');
    this.mobileToggle = this.querySelector('[data-mobile-toggle]');
    this.searchModal = this.querySelector('[data-search-modal]');
    this.searchOpen = this.querySelector('[data-search-open]');
    this.searchClose = this.querySelector('[data-search-close]');
    this.searchInput = this.querySelector('[data-search-input]');

    if (this.mobileToggle) {
      this.mobileToggle.addEventListener('click', this.openDrawer);
    }
    if (this.drawerClose) {
      this.drawerClose.addEventListener('click', this.closeDrawer);
    }
    if (this.drawerOverlay) {
      this.drawerOverlay.addEventListener('click', this.closeDrawer);
    }
    if (this.searchOpen) {
      this.searchOpen.addEventListener('click', this.openSearch);
    }
    if (this.searchClose) {
      this.searchClose.addEventListener('click', this.closeSearch);
    }
    if (this.searchModal) {
      this.searchModal.addEventListener('click', (event) => {
        if (event.target === this.searchModal) {
          this.closeSearch();
        }
      });
    }

    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('keydown', this.onKeydown);
  }

  onScroll() {
    this.classList.toggle('is-scrolled', window.scrollY > 20);
  }

  openDrawer() {
    if (!this.drawer) return;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  openSearch() {
    if (!this.searchModal) return;
    this.searchModal.classList.add('is-open');
    this.searchModal.setAttribute('aria-hidden', 'false');
    if (this.searchInput) {
      setTimeout(() => this.searchInput.focus(), 100);
    }
  }

  closeSearch() {
    if (!this.searchModal) return;
    this.searchModal.classList.remove('is-open');
    this.searchModal.setAttribute('aria-hidden', 'true');
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    this.closeDrawer();
    this.closeSearch();
  }
}

class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.headerIsAlwaysSticky =
      this.getAttribute('data-sticky-type') === 'always' ||
      this.getAttribute('data-sticky-type') === 'reduce-logo-size';
    this.headerBounds = {};

    this.setHeaderHeight();

    window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

    if (this.headerIsAlwaysSticky && this.header) {
      this.header.classList.add('shopify-section-header-sticky');
    }

    this.currentScrollTop = 0;
    this.preventReveal = false;

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, false);

    this.createObserver();
  }

  setHeaderHeight() {
    if (!this.header) return;
    document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  createObserver() {
    if (!this.header) return;

    const observer = new IntersectionObserver((entries, observerInstance) => {
      this.headerBounds = entries[0].intersectionRect;
      observerInstance.disconnect();
    });

    observer.observe(this.header);
  }

  onScroll() {
    if (!this.header) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const luxuryHeader = this.querySelector('luxury-header');
    const searchOpen = luxuryHeader?.querySelector('[data-search-modal].is-open');

    if (searchOpen) return;

    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);
        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);
        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      this.header.classList.remove('scrolled-past-header');
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (!this.header || this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    this.closeMenuDisclosure();
    this.closeSearchModal();
  }

  reveal() {
    if (!this.header || this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
  }

  reset() {
    if (!this.header || this.headerIsAlwaysSticky) return;
    this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
  }

  closeMenuDisclosure() {
    const luxuryHeader = this.querySelector('luxury-header');
    if (luxuryHeader) {
      luxuryHeader.closeDrawer();
    }
  }

  closeSearchModal() {
    const luxuryHeader = this.querySelector('luxury-header');
    if (luxuryHeader) {
      luxuryHeader.closeSearch();
    }
  }
}

if (!customElements.get('luxury-header')) {
  customElements.define('luxury-header', LuxuryHeader);
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}
